/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/*
 * Developer note! When updating this file, make sure to also update the
 * _app-config template files in pwa-kit-create-app.
 *
 * In the pwa-kit-create-app, the templates are found under:
 * - assets/bootstrap/js/overrides/app/components/_app-config
 * - assets/templates/@salesforce/retail-react-app/app/components/_app-config
 */
import React, {useMemo, useReducer} from 'react';
import PropTypes from 'prop-types';

import {ReactQueryDevtools} from '@tanstack/react-query-devtools';

import {CommerceApiProvider} from '@salesforce/commerce-sdk-react';
import {withReactQuery} from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-react-query';
import {useCorrelationId} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import createLogger from '@salesforce/pwa-kit-runtime/utils/logger-factory';
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import {ChakraProvider} from '@salesforce/retail-react-app/app/components/shared/ui';
import {getDefaultDnt} from '@amblique/king-living/app/utils/tracking-consent';
import CookiebotConsent from '@amblique/king-living/app/components/cookiebot-consent';
import {MultiSiteProvider} from '@salesforce/retail-react-app/app/contexts';
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';
import theme from '@salesforce/retail-react-app/app/theme';
import {resolveLocaleFromUrl, resolveSiteFromUrl} from '@salesforce/retail-react-app/app/utils/site-utils';
import {createUrlTemplate} from '@salesforce/retail-react-app/app/utils/url';
import {HelmetProvider} from 'react-helmet-async';

import {CrmProvider} from '@amblique/king-living/app/contexts/crm-context';
import {GlobalConfigProvider} from '@amblique/king-living/app/contexts/global-config-context'; // Save data of Contentful GlobalConfig into context
import {prefetchDynamicPageSeoQueries} from '@amblique/king-living/app/contentful/hooks/use-page';

// Removes focus for non-keyboard interactions for the whole application
import 'focus-visible/dist/focus-visible';

/**
 * Use the AppConfig component to inject extra arguments into the getProps
 * methods for all Route Components in the app – typically you'd want to do this
 * to inject a connector instance that can be used in all Pages.
 *
 * You can also use the AppConfig to configure a state-management library such
 * as Redux, or Mobx, if you like.
 */
const AppConfig = ({children, locals = {}}) => {
  const [, refreshTracking] = useReducer((version) => version + 1, 0);
  const commerceSdkLogger = useMemo(() => createLogger({packageName: 'commerce-sdk-react'}), []);
  const {correlationId} = useCorrelationId();
  const headers = {
    'correlation-id': correlationId,
  };

  const commerceApiConfig = locals.appConfig.commerceAPI;

  const appOrigin = useAppOrigin();

  const passwordlessCallback = locals.appConfig.login?.passwordless?.callbackURI;

  const helmetContext =
    typeof global !== 'undefined' && global.__HELMET_ASYNC_CONTEXT__ ?
      global.__HELMET_ASYNC_CONTEXT__ :
      undefined;

  return (
    <HelmetProvider context={helmetContext}>
      <CommerceApiProvider
        clientId={commerceApiConfig.parameters.clientId}
        currency={locals.locale?.preferredCurrency}
        defaultDnt={getDefaultDnt(locals.site)}
        enablePWAKitPrivateClient={true}
        headers={headers}
        locale={locals.locale?.id}
        logger={commerceSdkLogger}
        organizationId={commerceApiConfig.parameters.organizationId}
        passwordlessLoginCallbackURI={passwordlessCallback}
        proxy={`${appOrigin}${commerceApiConfig.proxyPath}`}
        redirectURI={`${appOrigin}/callback`}
        shortCode={commerceApiConfig.parameters.shortCode}
        siteId={locals.site?.id}
      >
        <MultiSiteProvider buildUrl={locals.buildUrl} locale={locals.locale} site={locals.site}>
          <CookiebotConsent onDntSynchronized={refreshTracking} onDntUpdating={refreshTracking} />
          <CrmProvider>
            <GlobalConfigProvider>
              <ChakraProvider theme={theme}>{children}</ChakraProvider>
            </GlobalConfigProvider>
          </CrmProvider>
        </MultiSiteProvider>
        <ReactQueryDevtools />
      </CommerceApiProvider>
    </HelmetProvider>
  );
};

AppConfig.restore = (locals = {}) => {
  const path =
    typeof window === 'undefined' ?
      locals.originalUrl :
      `${window.location.pathname}${window.location.search}`;

  const site = resolveSiteFromUrl(path);
  const locale = resolveLocaleFromUrl(path);

  const {app: appConfig} = getConfig();
  const apiConfig = {
    ...appConfig.commerceAPI,
    einsteinConfig: appConfig.einsteinAPI,
  };

  apiConfig.parameters.siteId = site.id;

  locals.buildUrl = createUrlTemplate(appConfig, site.alias || site.id, locale.id);
  locals.site = site;
  locals.locale = locale;
  locals.appConfig = appConfig;

  if (typeof global !== 'undefined') {
    global.__SSR_ACTIVE_SITE__ = site;
  }
};

AppConfig.freeze = () => undefined;

AppConfig.extraGetPropsArgs = (locals = {}) => {
  return {
    buildUrl: locals.buildUrl,
    site: locals.site,
    locale: locals.locale,
  };
};

AppConfig.propTypes = {
  children: PropTypes.node,
  locals: PropTypes.object,
};

const isServerSide = typeof window === 'undefined';
// Recommended settings for PWA-Kit usages.
// NOTE: they will be applied on both server and client side.
// retry is always disabled on server side regardless of the value from the options
const options = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 10 * 1000,
        ...(isServerSide ? {retryOnMount: false} : {}),
      },
      mutations: {
        retry: false,
      },
    },
  },
  beforeHydrate: (data) => {
    const now = Date.now();

    // Helper to reset the data timestamp to time of app load.
    const updateQueryTimeStamp = ({state}) => {
      state.dataUpdatedAt = now;
    };

    // Update serialized mutations and queries to ensure that the cached data is
    // considered fresh on first load.
    data?.mutations?.forEach(updateQueryTimeStamp);
    data?.queries?.forEach(updateQueryTimeStamp);

    return data;
  },
};

const AppConfigWithReactQuery = withReactQuery(AppConfig, options);

const baseGetInitializers = AppConfigWithReactQuery.getInitializers.bind(AppConfigWithReactQuery);

AppConfigWithReactQuery.getInitializers = function getInitializers() {
  const downstreamInitializers = baseGetInitializers();

  return [
    async (args) => {
      // Must finish before react-query prepass so page (and dependent useProduct) are in cache.
      await prefetchDynamicPageSeoQueries(args);

      const results = await Promise.all(downstreamInitializers.map((fn) => fn(args)));

      return Object.assign({}, ...results);
    }
  ];
};

export default AppConfigWithReactQuery;
